import { Component, effect, inject, signal } from '@angular/core';
import { AdresseAutocompleteComponent } from '@lib/components/adresse-autocomplete/adresse-autocomplete.component';
import {
  OuiAutocompleteOption,
  OuiDatePicker,
  OuiSelect,
  OuiTextfield,
  OuiValidationError,
} from 'omnium-ui/form-field';
import { OuiInfosBulle } from 'omnium-ui/infos-bulle';
import { DocumentFormBase } from '../document-form.base';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { siretErrors } from '@lib/validators/siret-errors-validators';
import { OptionsService } from '@lib/services/options/options.service';
import { Feature } from 'geojson';
import { sirenErrors } from '@lib/validators/siren-errors-validators';
import { DocumentsConsultantService } from '@lib/services/documents/documents-consultant.service';
import { tvaIntracommunautaireValidator } from '@lib/validators/tva-intracommunautaire.validator';
import { tvaIntracommunautaireValidatorErrors } from '@lib/validators/tva-intracommunautaire-errors-validators';
import { todayOrBeforeDateValidator } from '@lib/validators/today-or-before-date.validator';
import { dateValiditePjErrors } from '@lib/validators/date-validition-pj-errors-validators';
import { OuiLoading } from 'omnium-ui/loading';
import { NumbersRegex } from '@lib/components/controle-documents/models/regex-numbers.model';
import moment from 'moment';

@Component({
  selector: 'app-document-form-kbis',
  standalone: true,
  imports: [
    OuiDatePicker,
    OuiTextfield,
    OuiInfosBulle,
    OuiSelect,
    OuiLoading,
    AdresseAutocompleteComponent,
  ],
  templateUrl: './document-form-kbis.component.html',
  styleUrl: './document-form-kbis.component.scss',
})
export class DocumentFormKbisComponent extends DocumentFormBase<KbisForm> {
  private readonly optionsService = inject(OptionsService);
  private readonly documentConsultantService = inject(DocumentsConsultantService);

  readonly countriesOptions = signal<OuiAutocompleteOption<string>[]>([]);
  readonly formeJuridiqueOptions = this.optionsService.formeJuridiqueOptions;
  readonly statutJuridiquesConsultant = this.documentConsultantService.statutJuridiques;

  readonly sirenErrors: OuiValidationError[] = sirenErrors();
  readonly siretErrors: OuiValidationError[] = siretErrors();
  readonly tvaIntra: OuiValidationError[] = tvaIntracommunautaireValidatorErrors();
  readonly dateValidationErrors: OuiValidationError[] = dateValiditePjErrors();

  readonly isLoading = signal<boolean>(true);
  readonly isSociete = signal<boolean>(false);
  readonly statutJuridiquelabel = signal<string>('');
  readonly numbersRegex = NumbersRegex;

  constructor() {
    super();
    effect(() => {
      if (this.statutJuridiquesConsultant().length !== 0) {
        this.getStatutJuridique();
      }
    });

    effect(() => {
      if (this.codeConsultant() !== null) {
        this.setUpKnownData();
      } else {
        this.isLoading.set(false);
      }
    });

    this.optionsService
      .countriesOptions()
      .then(value => this.countriesOptions.set(value));
  }

  protected override createForm(fb: NonNullableFormBuilder) {
    return createKbisForm(fb);
  }

  onAdressSelect(feature: Feature) {
    const p = feature.properties;

    if (!p) return;

    this.form.patchValue({
      voie: (p['name'] as string) ?? '',
      codePostal: (p['postcode'] as string) ?? '',
      ville: (p['city'] as string) ?? '',
    });
  }

  async setUpKnownData() {
    const docInfo = await this.documentConsultantService.GetConsultantInfoForNewDoc(
      this.codeConsultant()!
    );

    this.form.patchValue({
      voie: docInfo.voie,
      codePostal: docInfo.codePostal ?? '',
      ville: docInfo.ville ?? '',
      pays: docInfo.pays ?? '',
      raisonSociale: docInfo.raisonSociale ?? '',
      formeJuridique: docInfo.formeJuridique?.toLocaleLowerCase() ?? '',
      numeroSiret: docInfo.numeroSiret ?? '',
      villeRegistreCommerce: docInfo.villeRegistreCommerce ?? '',
      numeroTva: docInfo.numeroTva ?? '',
    });

    this.isLoading.set(false);
  }

  getStatutJuridique() {
    this.isSociete.set(
      this.statutJuridiquesConsultant()?.some(st => st.id === 3) ? true : false
    );

    this.statutJuridiquelabel.set(
      this.statutJuridiquesConsultant()!.length === 1
        ? this.statutJuridiquesConsultant()![0].statutLibelle
        : this.statutJuridiquesConsultant()!.filter(st => st.id !== 1)[0].statutLibelle
    );

    if (!this.isSociete()) {
      this.form.get('formeJuridique')?.removeValidators(Validators.required);
      this.form.get('raisonSociale')?.removeValidators(Validators.required);

      // update forms validator
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        control?.updateValueAndValidity();
      });
    }
  }

  setDateFinDeValidite(newDate: moment.Moment | string | null) {
    if (newDate != null && typeof newDate != 'string') {
      const dateFinKbis = moment(newDate).add(6, 'months').toDate();
      const newDateFin = newDate ? dateFinKbis : undefined;

      this.form.get('dateFinValidite')?.setValue(newDateFin);
    }
  }
}

function createKbisForm(fb: NonNullableFormBuilder) {
  return fb.group({
    voie: fb.control<string | undefined>(undefined),
    codePostal: fb.control<string | undefined>(undefined),
    ville: fb.control<string | undefined>(undefined),
    pays: fb.control<string>('FRA'),
    raisonSociale: fb.control<string | undefined>(undefined, [Validators.required]),
    formeJuridique: fb.control<string | undefined>(undefined, [Validators.required]),
    capitalSocial: fb.control<string | undefined>(undefined),
    numeroSiren: fb.control<string | undefined>(undefined, [
      Validators.required,
      Validators.minLength(9),
      Validators.maxLength(9),
    ]),
    numeroSiret: fb.control<string | undefined>(undefined, [
      Validators.required,
      Validators.minLength(14),
      Validators.maxLength(14),
    ]),
    numeroTva: fb.control<string | undefined>(undefined, [
      tvaIntracommunautaireValidator(),
    ]),
    villeRegistreCommerce: fb.control<string | undefined>(undefined, [
      Validators.required,
    ]),
    dateDebutValidite: fb.control<Date | undefined>(undefined, [
      Validators.required,
      todayOrBeforeDateValidator(),
    ]),
    dateFinValidite: fb.control<Date | undefined>(undefined),
  });
}

export type KbisForm = ReturnType<typeof createKbisForm>;
export type KbisFormValue = KbisForm['value'];
